// client/src/components/gallery/PhotoCard.js
import React, { useState } from 'react';
import {
  Box, Typography, IconButton, Tooltip, Avatar
} from '@mui/material';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import axiosInstance from '../../axiosInstance';
import CommentThread from '../social/CommentThread';
import FollowButton from '../social/FollowButton';
import ReputationBadge from '../reputation/ReputationBadge';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

export default function PhotoCard({ photo, currentUserId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [liked, setLiked] = useState(
    photo.likes?.some(l => String(l.userId) === String(currentUserId)) || false
  );
  const [likeCount, setLikeCount] = useState(photo.likeCount || 0);
  const [disliked, setDisliked] = useState(
    photo.dislikes?.some(d => String(d.userId) === String(currentUserId)) || false
  );
  const [dislikeCount, setDislikeCount] = useState(photo.dislikeCount || 0);
  const [showComments, setShowComments] = useState(false);

  const user = photo.userId;
  const isOwn = String(user?._id) === String(currentUserId);

  const handleLike = async () => {
    try {
      if (liked) {
        await axiosInstance.delete(`/photos/${photo._id}/like`);
        setLiked(false); setLikeCount(c => c - 1);
      } else {
        await axiosInstance.post(`/photos/${photo._id}/like`);
        setLiked(true); setLikeCount(c => c + 1);
        if (disliked) { setDisliked(false); setDislikeCount(c => c - 1); }
      }
    } catch (err) { console.error('Photo like error:', err); }
  };

  const handleDislike = async () => {
    try {
      if (disliked) {
        await axiosInstance.delete(`/photos/${photo._id}/dislike`);
        setDisliked(false); setDislikeCount(c => c - 1);
      } else {
        const res = await axiosInstance.post(`/photos/${photo._id}/dislike`);
        setDisliked(true); setDislikeCount(c => c + 1);
        if (liked) { setLiked(false); setLikeCount(res.data.likeCount ?? likeCount - 1); }
      }
    } catch (err) { console.error('Photo dislike error:', err); }
  };

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
      <Box component="img" src={photo.imageUrl} alt={photo.caption || 'Item photo'}
        sx={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block' }} />
      <Box sx={{ p: 1.5 }}>
        {photo.caption && <Typography variant="body2" gutterBottom>{photo.caption}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar src={user?.profileImage} sx={{ width: 24, height: 24 }} />
          <Typography variant="caption" fontWeight={600}>{user?.username}</Typography>
          <ReputationBadge trustLevel={user?.trustLevel} />
          {isFeatureEnabled('enableFollowSystem') && (
            <FollowButton targetUserId={user?._id} currentUserId={currentUserId} size="small" />
          )}
          {!isOwn && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <IconButton size="small" onClick={handleLike}>
                {liked ? <ThumbUpIcon fontSize="small" color="primary" /> : <ThumbUpOutlinedIcon fontSize="small" />}
              </IconButton>
              {likeCount > 0 && <Typography variant="caption">{likeCount}</Typography>}
              <IconButton size="small" onClick={handleDislike}>
                {disliked ? <ThumbDownIcon fontSize="small" color="error" /> : <ThumbDownOutlinedIcon fontSize="small" />}
              </IconButton>
              {dislikeCount > 0 && <Typography variant="caption" color="text.secondary">{dislikeCount}</Typography>}
            </Box>
          )}
        </Box>
        {isFeatureEnabled('enableComments') && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" sx={{ cursor: 'pointer' }}
              onClick={() => setShowComments(s => !s)} color="primary">
              {showComments ? 'Hide comments' : 'Comments'}
            </Typography>
            {showComments && (
              <CommentThread targetType="photo" targetId={photo._id} currentUserId={currentUserId} />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
